import { N40, B300 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import React, { FunctionComponent, ReactNode, Suspense } from 'react';
import styled, { css } from 'styled-components';

import { dynamicImport } from '@canvas/dynamic-import';
import { testId } from '@canvas/testing/test-id';

const LazySmartCard = React.lazy(() =>
  dynamicImport(() =>
    import('@atlaskit/smart-card').then((module) => ({ default: module.Card }))
  )
);

const LazySmartHoverCard = React.lazy(() =>
  dynamicImport(() =>
    import('@atlaskit/smart-card/hover-card').then((module) => ({
      default: module.HoverCard,
    }))
  )
);

const Wrapper = styled.span.attrs((props) => ({
  'data-testid': props.testId,
}))`
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  box-sizing: border-box;
  padding: 1px 3px;
  margin: -1px 2px;
  white-space: nowrap;

  ${(props) =>
    props.as === 'a' &&
    css`
      border-radius: ${token('border.radius.100', '4px')};
      border: 1px solid ${token('color.border', N40)};

      :hover {
        border-color: ${token('color.border.accent.blue', B300)};
        text-decoration: none;
      }
    `}

  > * {
    display: inline-flex;
    align-items: center;
  }

  > .iconBefore {
    margin-right: 4px;
    flex-shrink: 0;
  }

  > .text {
    display: inline-block;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  > .iconAfter {
    margin-left: 4px;
    flex-shrink: 0;
  }
`;

export interface AbstractLinkProps {
  href?: string;
  iconBefore?: ReactNode;
  iconAfter?: ReactNode;
  noFocus?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  onFocus?: (e: React.FocusEvent) => void;
  /** Content to show if useSmartLink is false */
  children?: ReactNode;
  noResolve?: boolean;
  useSmartLink?: boolean;
}

export const validateUrl = (value: string): boolean => {
  try {
    new URL(value);
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Renders a formatted placeholder representing a link with an icon, for example a page reference.
 */
const AbstractLink: FunctionComponent<AbstractLinkProps> = ({
  href,
  noFocus,
  onClick,
  onFocus,
  children,
  iconBefore,
  iconAfter,
  noResolve,
  useSmartLink,
}) => {
  const content = (
    <>
      {iconBefore && <span className="iconBefore">{iconBefore}</span>}
      <span className="text">{children}</span>
      {iconAfter && <span className={'iconAfter'}>{iconAfter}</span>}
    </>
  );
  const fallbackComponent = (
    <Wrapper
      testId={testId('databases.components.content-link.abstract-link.fallback')}
      as="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
      onFocus={onFocus}
      tabIndex={noFocus ? -1 : undefined}
    >
      <span className="text">{href}</span>
    </Wrapper>
  );

  if (!href) {
    return (
      <Wrapper testId={testId('databases.components.content-link.abstract-link.wrapper')}>
        {content}
      </Wrapper>
    );
  }

  if (!useSmartLink) {
    return (
      <Suspense fallback={<Wrapper>{content}</Wrapper>}>
        <LazySmartHoverCard url={href}>
          <Wrapper
            testId={testId(
              'databases.components.content-link.abstract-link.lazy-smart-hover-card-no-smart-link'
            )}
            as="a"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onClick}
            onFocus={onFocus}
            tabIndex={noFocus ? -1 : undefined}
          >
            {content}
          </Wrapper>
        </LazySmartHoverCard>
      </Suspense>
    );
  }

  if (!validateUrl(href) || noResolve) {
    return fallbackComponent;
  }

  return (
    <Suspense fallback={<Wrapper>{content}</Wrapper>}>
      <LazySmartHoverCard url={href}>
        <LazySmartCard
          onClick={(event) => {
            event.preventDefault();
            window.open(href, '_blank', 'noopener, noreferrer');
          }}
          appearance="inline"
          showAuthTooltip
          url={href}
          fallbackComponent={() => fallbackComponent}
          truncateInline
        />
      </LazySmartHoverCard>
    </Suspense>
  );
};

export default AbstractLink;
